import { useCallback, useEffect, useState, useRef } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';

/**
 * Custom hook to block navigation and show a confirmation.
 *
 * @param {boolean} when - Whether to block navigation
 * @param {(nextLocation) => boolean} shouldBlockNavigation - Function to decide if a navigation should be blocked
 * @param {() => void} onConfirm - Callback when user confirms navigation
 */
export default function usePrompt(when, shouldBlockNavigation, onConfirm) {
  const [nextLocation, setNextLocation] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const blocker = useBlocker(when);
  const retryRef = useRef();
  const navigate = useNavigate();

  const retryNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset(); // Reset the blocker state to allow navigation from component
    }
    setConfirmed(true);
    setNextLocation(null);
  }, [blocker, navigate, nextLocation]);

  // Always keep the latest retry function
  retryRef.current = retryNavigation;

  const stableRetry = useCallback(() => {
    retryRef.current();
  }, []);

  const cancelNavigation = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    setNextLocation(null);
  }, [blocker]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const shouldBlock = shouldBlockNavigation(blocker.location);
      if (shouldBlock && !confirmed) {
        if (!nextLocation) {
          setNextLocation(blocker.location);
        }
        onConfirm(stableRetry, cancelNavigation);
      } else {
        if (blocker.state === 'blocked') {
          blocker.proceed(); // continue navigation immediately
        }
        setConfirmed(false);
      }
    }
  }, [
    blocker,
    shouldBlockNavigation,
    onConfirm,
    stableRetry,
    cancelNavigation,
  ]);

  return nextLocation;
}
